import {
  APP_CLIENT_HEADER,
  APP_VERSION_HEADER,
  HTTP_UPGRADE_REQUIRED,
  RUNTIME_VERSION_HEADER,
  t,
} from '@aquabox/shared';

/**
 * API istemcisi.
 *
 * - Her istek sürüm başlıklarını taşır (CLAUDE.md kural 12). Sunucu 426 dönerse panel kilitlenir.
 * - Access token bellekte, refresh token localStorage'da tutulur: XSS'te access token'a erişmek
 *   zorlaşır, sekme yenilenince oturum refresh ile geri gelir.
 * - 401'de refresh bir kez denenir; başarısızsa oturum kapanır.
 * - Para alanları sunucudan string gelir; panel HESAP YAPMAZ, yalnızca biçimler (kural 4).
 *
 * BİLİNEN SINIR (Faz 1 denetimi U4, ÇALIŞTIRILARAK doğrulandı): `refreshing` promise paylaşımı
 * yalnızca AYNI sekme/JS heap içindeki eşzamanlı 401'leri tekilleştirir. Panel iki sekmede açıkken
 * (patronun panoyu bir sekmede, siparişleri başka sekmede tutması olağan) ve ikisi de aynı anda
 * uyanırsa (laptop uyku modundan çıkış, art arda F5), her sekmenin KENDİ `refreshing` kilidi var —
 * localStorage'daki refresh token PAYLAŞILIR ama kilit paylaşılmaz. İki sekme aynı eski refresh
 * token'la eşzamanlı yenileme dener; backend (auth.service.ts#refresh) kaybedeni "yeniden kullanım"
 * sayıp TÜM AİLEYİ iptal eder — kazanan sekme çalışmaya devam eder, kaybeden sessizce login ekranına
 * düşer. Tam çözüm (BroadcastChannel/storage-event tabanlı sekmeler-arası kilit) Faz 2'ye bırakıldı;
 * bu ölçekte (birkaç kullanıcı, nadiren çoklu sekme) düşük öncelikli bir UX kusuru, güvenlik açığı
 * değil (aile iptali doğru çalışıyor, veri sızmıyor).
 */
const REFRESH_KEY = 'aquabox.refresh';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Sunucunun makine okunur hata kodu (örn. `CASH_COUNT_RETRY`); gövdede yoksa undefined. */
  get code(): string | undefined {
    const code = (this.body as { code?: unknown } | null)?.code;
    return typeof code === 'string' ? code : undefined;
  }

  /** Alan bazlı doğrulama hataları (ZodPipe). */
  get issues(): { path: string; message: string }[] {
    const body = this.body as { issues?: { path: string; message: string }[] } | null;
    return body?.issues ?? [];
  }
}

export class UpgradeRequiredError extends ApiError {}

type Listener = () => void;

class ApiClient {
  private accessToken: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  private readonly authListeners = new Set<Listener>();
  private readonly upgradeListeners = new Set<Listener>();

  get isAuthenticated(): boolean {
    return this.accessToken !== null;
  }

  get hasRefreshToken(): boolean {
    return this.readRefresh() !== null;
  }

  onAuthChange(fn: Listener): () => void {
    this.authListeners.add(fn);
    return () => this.authListeners.delete(fn);
  }

  onUpgradeRequired(fn: Listener): () => void {
    this.upgradeListeners.add(fn);
    return () => this.upgradeListeners.delete(fn);
  }

  async login(email: string, password: string): Promise<void> {
    const tokens = await this.request<{ accessToken: string; refreshToken: string }>(
      '/api/auth/login',
      {
        method: 'POST',
        body: { email, password },
        skipAuth: true,
      },
    );
    this.setTokens(tokens.accessToken, tokens.refreshToken);
  }

  async logout(): Promise<void> {
    const refreshToken = this.readRefresh();
    this.clearTokens();
    if (refreshToken) {
      try {
        await this.request('/api/auth/logout', {
          method: 'POST',
          body: { refreshToken },
          skipAuth: true,
        });
      } catch {
        // Oturum yerelde kapandı; sunucu hatası kullanıcıyı ilgilendirmez.
      }
    }
  }

  /** Sayfa yenilendiğinde refresh token ile oturumu geri alır. */
  async restoreSession(): Promise<boolean> {
    if (this.accessToken) return true;
    if (!this.readRefresh()) return false;
    return this.refresh();
  }

  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'GET', signal });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body, signal });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  /**
   * Kimlik ve sürüm başlıklarıyla ikili (binary) içerik indirir; makbuz PDF'i için.
   *
   * Düz `<a href="/api/receipts/:id/pdf">` ÇALIŞMAZ: access token bellekte durur ve tarayıcı
   * bağlantısına eklenmez, üstelik sürüm başlıkları da gitmez — sunucu 400 döner. Bu yüzden
   * PDF `fetch` ile alınıp blob olarak açılır (Faz 3 denetimi U4).
   */
  async getBlobUrl(path: string): Promise<string> {
    const res = await fetch(path, {
      headers: {
        [APP_VERSION_HEADER]: __APP_VERSION__,
        [RUNTIME_VERSION_HEADER]: __RUNTIME_VERSION__,
        [APP_CLIENT_HEADER]: 'panel',
        ...(this.accessToken ? { authorization: `Bearer ${this.accessToken}` } : {}),
      },
    });
    if (!res.ok) {
      // Aynı çıkarma deseni `request()`'teki JSON yolla: gövde ayrıştırılabiliyorsa ve
      // `message` alanı varsa sunucunun kendi (Türkçe) mesajı gösterilir — ör. 400'de aralık
      // sınırı metni. HTML hata sayfası, boş gövde ya da ayrıştırılamayan içerikte `parseBody`
      // zaten `null`/ham metin döner, `message` bulunamaz ve genel mesaja düşülür.
      const body: unknown = await this.parseBody(res);
      const message = (body as { message?: string } | null)?.message ?? t('error.internal');
      throw new ApiError(res.status, body, message);
    }
    return URL.createObjectURL(await res.blob());
  }

  private async request<T>(
    path: string,
    opts: {
      method: string;
      body?: unknown;
      skipAuth?: boolean;
      retried?: boolean;
      signal?: AbortSignal;
    },
  ): Promise<T> {
    const headers: Record<string, string> = {
      [APP_VERSION_HEADER]: __APP_VERSION__,
      [RUNTIME_VERSION_HEADER]: __RUNTIME_VERSION__,
      [APP_CLIENT_HEADER]: 'panel',
    };
    if (opts.body !== undefined) headers['content-type'] = 'application/json';
    if (!opts.skipAuth && this.accessToken) headers.authorization = `Bearer ${this.accessToken}`;

    const res = await fetch(path, {
      method: opts.method,
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });

    if (res.status === HTTP_UPGRADE_REQUIRED) {
      const body: unknown = await this.parseBody(res);
      for (const fn of this.upgradeListeners) fn();
      throw new UpgradeRequiredError(res.status, body, t('version.upgradeRequired'));
    }

    if (res.status === 401 && !opts.skipAuth && !opts.retried) {
      const ok = await this.refresh();
      if (ok) return this.request<T>(path, { ...opts, retried: true });
      this.clearTokens();
    }

    if (res.status === 204) return undefined as T;

    const body: unknown = await this.parseBody(res);
    if (!res.ok) {
      const message = (body as { message?: string } | null)?.message ?? t('error.internal');
      throw new ApiError(res.status, body, message);
    }
    return body as T;
  }

  private async parseBody(res: Response): Promise<unknown> {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }

  /** Eşzamanlı 401'lerde tek refresh isteği atılır (yarış yok). */
  private refresh(): Promise<boolean> {
    this.refreshing ??= this.doRefresh().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = this.readRefresh();
    if (!refreshToken) return false;
    try {
      const tokens = await this.request<{ accessToken: string; refreshToken: string }>(
        '/api/auth/refresh',
        {
          method: 'POST',
          body: { refreshToken },
          skipAuth: true,
        },
      );
      this.setTokens(tokens.accessToken, tokens.refreshToken);
      return true;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  private setTokens(access: string, refresh: string): void {
    this.accessToken = access;
    try {
      localStorage.setItem(REFRESH_KEY, refresh);
    } catch {
      // Gizli sekmede localStorage kapalı olabilir; oturum yalnız bu sekmede yaşar.
    }
    for (const fn of this.authListeners) fn();
  }

  private clearTokens(): void {
    this.accessToken = null;
    try {
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      // yoksay
    }
    for (const fn of this.authListeners) fn();
  }

  private readRefresh(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  }
}

export const api = new ApiClient();
